/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { flow } from 'lodash'

import { sanitizeCsvField } from '@modules/utils/sanitizeCsvField'

const COMMA = ','

const LINE_BREAK = '\n'

/**
 * Applies a function to each row of a CSV string. Only row order is preserved, so any changes to the columns
 * (including column order) will be reflected in the output.
 *
 * @param csv - The CSV string to process.
 * @param rowFn - The function to apply to each row.
 * @returns The processed CSV string.
 */
export function mapRows(
  csv: string,
  rowFn: (cols: string[], isHeader: boolean) => string[],
): string {
  const allRows = csv.split(LINE_BREAK)

  for (let r = 0; r < allRows.length; r++) {
    const row = allRows[r]

    if (!row) {
      continue
    }

    const cols = row.split(COMMA)

    allRows[r] = rowFn(cols, r === 0).join(COMMA)
  }

  return allRows.join(LINE_BREAK)
}

export function makeClusterIdColumnLast(csv: string): string {
  return moveColumnByName(csv, 'id', -1)
}

export function makeSelfManagedClusterIdColumnFirst(csv: string): string {
  return moveColumnByName(csv, 'self_managed_cluster.id', 0)
}

/**
 * Moves a column by name to a given position.
 *
 * @param csv - The CSV string to process.
 * @param columnName - The name of the column to move.
 * @param to - The position to move the column to. A negative value -x means to move the column to the x-th position
 * from the end, e.g. -1 moves the column to the last position.
 * @returns The CSV string with the column moved. If the column is not found, the original CSV is returned.
 */
export function moveColumnByName(csv: string, columnName: string, to: number): string {
  const from = findHeaderColumnIndex(csv, columnName)

  if (from === -1) {
    return csv
  }

  return moveColumn(csv, from, to)
}

/**
 * Moves a column from a given position to a given position.
 *
 * @param csv - The CSV string to process.
 * @param from - The position of the column to move.
 * @param to - The position to move the column to. A negative value -x means to move the column to the x-th position
 * from the end, e.g. -1 moves the column to the last position.
 * @returns The CSV string with the column moved. If the from or to positions are out of bounds, the original CSV is returned.
 */
export function moveColumn(csv: string, from: number, to: number): string {
  const headerCols = getHeaderColumns(csv)

  if (from < 0 || from >= headerCols.length) {
    return csv
  }

  const actualDestination = to >= 0 ? to : headerCols.length + to

  if (actualDestination < 0 || actualDestination >= headerCols.length) {
    return csv
  }

  return mapRows(csv, (cols) => {
    movePosition(cols, from, actualDestination)
    return cols
  })
}

export function sanitizeClusterName(csv: string): string {
  const columnIndex = findHeaderColumnIndex(csv, 'self_managed_cluster.name')

  return mapRows(csv, (cols, isHeader) => {
    if (isHeader) {
      return cols
    }

    if (cols[columnIndex] === undefined) {
      return cols
    }

    cols[columnIndex] = sanitizeCsvField(cols[columnIndex])

    return cols
  })
}

export function removeHeader(csv: string): string {
  return csv.slice(csv.indexOf(LINE_BREAK) + 1)
}

export function formatDateColumns(csv: string): string {
  const dateColumnIndexes = findDateColumnIndices(getHeaderColumns(csv))

  return mapRows(csv, (cols, isHeader) => {
    if (isHeader) {
      return cols
    }

    for (const idx of dateColumnIndexes) {
      if (!cols[idx]) {
        continue
      }

      cols[idx] = formatDate(cols[idx])
    }

    return cols
  })
}

export function findHeaderColumnIndex(csv: string, column: HeaderColumn): number {
  return getHeaderColumns(csv).findIndex((col) => col === column)
}

export function getHeaderColumns(csv: string): string[] {
  const header = csv.slice(0, csv.indexOf(LINE_BREAK))

  return header.split(COMMA)
}

export function translateHeader(csv: string): string {
  const header = csv.slice(0, csv.indexOf(LINE_BREAK))

  const rest = csv.slice(csv.indexOf(LINE_BREAK) + 1)

  return [header.split(COMMA).map(translateHeaderColumn).join(COMMA), rest].join(LINE_BREAK)
}

export const translateHeaderColumn = (column: string) =>
  HUMAN_FRIENDLY_HEADER_COLUMNS[column as HeaderColumn] ?? column

const HEADER_COLUMNS = [
  'id',
  'license.type',
  'license.id',
  'cluster_metadata.created_at',
  'cluster_metadata.created_by',
  'cluster_metadata.deleted_at',
  'cluster_metadata.deleted_by',
  'cluster_metadata.deleted_reason',
  'cluster_metadata.last_updated_at',
  'cluster_metadata.last_updated_by',
  'cluster_metadata.organization_id',
  'name',
  'self_managed_cluster.id',
  'self_managed_cluster.name',
  'self_managed_cluster.version',
  'services.auto_ops.enabled',
  'services.auto_ops.disconnection_timestamp',
]

type HeaderColumn = (typeof HEADER_COLUMNS)[number]

const DATE_COLUMNS: Set<HeaderColumn> = new Set([
  'cluster_metadata.created_at',
  'cluster_metadata.deleted_at',
  'cluster_metadata.last_updated_at',
  'services.auto_ops.disconnection_timestamp',
  'services.eis.disconnection_timestamp',
])

const HUMAN_FRIENDLY_HEADER_COLUMNS: Record<HeaderColumn, string> = {
  id: 'Resource ID',
  'license.type': 'License Type',
  'license.id': 'License ID',
  'cluster_metadata.created_at': 'Created At',
  'cluster_metadata.created_by': 'Created By',
  'cluster_metadata.deleted_at': 'Deleted At',
  'cluster_metadata.deleted_by': 'Deleted By',
  'cluster_metadata.deleted_reason': 'Deleted Reason',
  'cluster_metadata.last_updated_at': 'Last Updated At',
  'cluster_metadata.last_updated_by': 'Last Updated By',
  'cluster_metadata.organization_id': 'Organization ID',
  name: 'Cluster Name',
  'self_managed_cluster.id': 'Self-Managed Cluster ID',
  'self_managed_cluster.name': 'Self-Managed Cluster Name',
  'self_managed_cluster.version': 'Self-Managed Cluster Version',
  'services.auto_ops.enabled': 'AutoOps Enabled',
  'cluster_metadata.created_by_testing_user': 'Created By a Test User',
  'services.auto_ops.disconnection_timestamp': 'AutoOps Disconnected At',
  'services.eis.enabled': 'EIS Enabled',
  'services.eis.disconnection_timestamp': 'EIS Disconnected At',
}

const options: Intl.DateTimeFormatOptions = {
  dateStyle: 'short',
  timeStyle: 'medium',
  hour12: false,
}

// Create a formatter and apply it to the date
// 'en-US' is used here, but it can be adapted for other locales
const formatter = new Intl.DateTimeFormat('en-US', options)

export function findDateColumnIndices(headerCols: string[]): number[] {
  const dateColumnIndices: number[] = []

  for (let i = 0; i < headerCols.length; i++) {
    if (DATE_COLUMNS.has(headerCols[i] as HeaderColumn)) {
      dateColumnIndices.push(i)
    }
  }

  return dateColumnIndices
}

export function formatDate(isoDate: string) {
  try {
    return formatter
      .formatToParts(new Date(isoDate))
      .map(({ value }) => value.replaceAll(', ', ' '))
      .join('')
  } catch (error) {
    return isoDate
  }
}

export function movePosition(cols: unknown[], from: number, to: number) {
  if (from < 0 || from >= cols.length || to < 0 || to >= cols.length) {
    return
  }

  const fromValue = cols[from]

  if (from < to) {
    cols.copyWithin(from, from + 1, to + 1)
  }

  if (from > to) {
    cols.copyWithin(to + 1, to, from)
  }

  cols[to] = fromValue
}

export async function processCSVPages(asyncIterator: AsyncIteratorObject<string>) {
  const csvParts: string[] = []

  const processor = flow(
    sanitizeClusterName,
    formatDateColumns,
    makeSelfManagedClusterIdColumnFirst,
    makeClusterIdColumnLast,
  )

  let firstPage = true

  for await (const csvPage of asyncIterator) {
    const processedCSVPage = processor(csvPage.trimEnd())

    if (firstPage) {
      csvParts.push(translateHeader(processedCSVPage))
    } else {
      csvParts.push(removeHeader(processedCSVPage))
    }

    firstPage = false
  }

  return csvParts.join('\n')
}
